/**
 * Capture the `?address=` research-link pre-fill hint and strip it from the URL
 * BEFORE analytics initialize. Left in the URL, a home address is swept into
 * page-URL analytics (Mixpanel attaches `$current_url` to every event) and
 * browser history. Imported first in `index.tsx` (before `AnalyticsProvider`),
 * this runs at import time, holds the value in memory for `useResearchParams`
 * (so the guest pre-fill still works), and rewrites the URL without `address`.
 * Only `address` is stripped; `campaignCode`/`participantId` (non-PII) stay.
 */

const ADDRESS_PARAM = 'address';

let capturedAddress: string | null = null;

/**
 * Remove the `address` query param from an href, returning the captured value
 * and the cleaned href. Pure (no side effects) so it can be unit-tested.
 */
export function extractAndStripAddress(href: string): {
  address: string | null;
  cleanedHref: string;
} {
  const url = new URL(href);
  const address = url.searchParams.get(ADDRESS_PARAM);
  if (address === null) return { address: null, cleanedHref: href };
  url.searchParams.delete(ADDRESS_PARAM);
  return { address, cleanedHref: url.pathname + url.search + url.hash };
}

function bootstrap(): void {
  if (typeof window === 'undefined' || !window.location) return;
  try {
    const { address, cleanedHref } = extractAndStripAddress(window.location.href);
    if (address === null) return;
    // Capture first so the pre-fill survives even if the URL rewrite throws.
    capturedAddress = address;
    try {
      window.history.replaceState(window.history.state, '', cleanedHref);
    } catch {
      // Scrub is best-effort; the captured value still drives the pre-fill.
    }
  } catch {
    // URL parsing failed — leave the location untouched.
  }
}

bootstrap();

/** The `?address=` value captured at initial page load, or `null`. */
export function getCapturedResearchAddress(): string | null {
  return capturedAddress;
}
